import React from "react";
import { Image as ImageIcon } from "lucide-react";
import AIStudioTool from "./AIStudioTool";

const FIELDS = [
  { key: "product_name", label: "Product name", required: true, wide: true,
    placeholder: "e.g. Sunrise Oats — Vanilla Almond" },

  { key: "brand_name", label: "Brand name",
    placeholder: "Optional. Helps the AI reference the brand voice." },

  { key: "ad_headline", label: "On-image headline",
    placeholder: "e.g. 'Real ingredients, real mornings.'",
    hint: "Optional. If provided, the AI will render this text on the creative.",
    maxLength: 140 },

  { key: "format", label: "Ad format", type: "select",
    options: [
      { value: "square",      label: "Square (1:1) — feed" },
      { value: "portrait",    label: "Portrait (4:5) — feed" },
      { value: "landscape",   label: "Landscape (1.91:1) — link ads" },
      { value: "story",       label: "Story / Reel (9:16)" },
      { value: "wide_banner", label: "Wide banner (2:1)" },
    ] },

  { key: "platform", label: "Platform", type: "select",
    options: [
      { value: "meta",           label: "Meta (Facebook/Instagram)" },
      { value: "instagram",      label: "Instagram only" },
      { value: "tiktok",         label: "TikTok" },
      { value: "reddit",         label: "Reddit" },
      { value: "google_display", label: "Google Display" },
      { value: "generic",        label: "Generic / multi-platform" },
    ] },

  { key: "aspirational_brands", label: "Aspirational brands", wide: true,
    placeholder: "e.g. Olipop, Poppi, Liquid Death, Magic Spoon",
    hint: "Comma-separated brands whose ad aesthetic to evoke.",
    maxLength: 200 },

  { key: "photography_style", label: "Photography style", type: "select",
    options: [
      { value: "studio_product",      label: "Studio product" },
      { value: "editorial_lifestyle", label: "Editorial lifestyle" },
      { value: "retail_shelf",        label: "Retail shelf" },
      { value: "flat_lay",            label: "Flat lay" },
      { value: "hero_hero",           label: "Hero-hero — single dramatic subject" },
    ] },

  { key: "typography_direction", label: "Typography direction", type: "select",
    options: [
      { value: "modern_grotesk",       label: "Modern grotesk" },
      { value: "editorial_serif",      label: "Editorial serif" },
      { value: "hand_drawn",           label: "Hand-drawn / marker" },
      { value: "vintage_script",       label: "Vintage script" },
      { value: "all_caps_industrial",  label: "All-caps industrial" },
      { value: "custom_wordmark",      label: "Custom wordmark" },
    ] },

  { key: "style", label: "Style direction",
    placeholder: "e.g. editorial, playful, luxury heritage, retro grocery ad",
    hint: "Free-form mood." },

  { key: "color_notes", label: "Colour notes",
    placeholder: "e.g. terracotta + cream, or navy + neon lime" },

  { key: "count",   label: "Creatives to generate", type: "number", min: 1, max: 4 },
  { key: "quality", label: "Render quality", type: "select",
    options: [
      { value: "low",    label: "Low — fastest" },
      { value: "medium", label: "Medium — balanced" },
      { value: "high",   label: "High — portfolio-ready (recommended)" },
    ] },
];

const DEFAULTS = {
  brand_name: "", product_name: "", ad_headline: "",
  format: "square", platform: "meta",
  aspirational_brands: "",
  photography_style: "editorial_lifestyle",
  typography_direction: "modern_grotesk",
  style: "editorial, on-brand, high contrast",
  color_notes: "",
  count: 3, quality: "medium",
};

const DEMO_INPUTS = [
  {
    label: "Beverage feed ad · Meta portrait",
    hint: "Editorial lifestyle · morning hero shot",
    values: {
      product_name: "Sunrise Oat — Vanilla Chamomile", brand_name: "Sunrise Oat",
      ad_headline: "Morning, done differently.", format: "portrait", platform: "meta",
      aspirational_brands: "Oatly, Liquid Death, Ghia", photography_style: "editorial_lifestyle",
      typography_direction: "editorial_serif",
      style: "editorial lifestyle, morning kitchen light, aspirational-but-honest",
      color_notes: "warm cream + terracotta", count: 3, quality: "medium",
    },
  },
  {
    label: "Snack TikTok · square",
    hint: "Loud color · high energy · Gen-Z",
    values: {
      product_name: "Chip Riot — Cool Ranch Storm", brand_name: "Chip Riot",
      ad_headline: "COOL RANCH · STORM MODE", format: "square", platform: "tiktok",
      aspirational_brands: "Doritos, Takis, Flamin' Hot", photography_style: "hero_hero",
      typography_direction: "all_caps_industrial",
      style: "high-saturation snack advert, chrome typography, retro-rave energy",
      color_notes: "electric blue #1A6DFF, cyan, hot pink on black", count: 3, quality: "medium",
    },
  },
  {
    label: "Supplement story · 9:16",
    hint: "Athletic-Greens minimal · morning ritual",
    values: {
      product_name: "Field & Marrow — Daily Adaptogen Blend", brand_name: "Field & Marrow",
      ad_headline: "12 clinical adaptogens. One scoop.", format: "story", platform: "instagram",
      aspirational_brands: "Athletic Greens, Moon Juice, Sakara", photography_style: "studio_product",
      typography_direction: "modern_grotesk",
      style: "medical-clean, apothecary meets modern lab, cool serene",
      color_notes: "sage + bone, matte black cap", count: 3, quality: "medium",
    },
  },
];

export default function AIStudioAdsDesigner() {
  return (
    <AIStudioTool
      toolSlug="ads-designer"
      name="Ads Designer"
      Icon={ImageIcon}
      tagline="Generate on-brand ad creatives across every format — square, story, landscape, banner — for any platform. Add a headline and Warren renders it right into the creative. Drop reference ads to anchor the aesthetic."
      fields={FIELDS}
      endpoint="/ai-studio/ads-designer/generate"
      defaultValues={DEFAULTS}
      demoInputs={DEMO_INPUTS}
      cta="Generate ad creatives"
      historyLabel="Your ad creatives"
      supportsReferences
      formHeading="Brief the creative"
    />
  );
}
